/**
 * Constantes de dominio.
 *
 * Se usan uniones de string en lugar de enums nativos para que el esquema sea
 * portable entre SQLite (desarrollo) y PostgreSQL (producción) sin migraciones
 * de tipo, y para que el mismo catálogo viaje al frontend y al móvil.
 */

/* ────────────────────────── Roles (spec §7.1) ────────────────────────── */

export const ROLES = [
  'owner',                 // Propietario de la empresa
  'superuser_business',    // Superusuario empresarial
  'superuser_finance',     // Superusuario financiero
  'superuser_contract',    // Superusuario contractual
  'admin',                 // Administrador
  'director',              // Director
  'sales',                 // Comercial
  'accounting',            // Contabilidad
  'compliance',            // Compliance
  'operations',            // Operaciones
  'purchasing',            // Compras
  'standard',              // Usuario estándar
  'readonly',              // Solo lectura
  'auditor',               // Auditor externo
  'client_guest',          // Cliente invitado
  'supplier_guest',        // Proveedor invitado
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  superuser_business: 'Business Superuser',
  superuser_finance: 'Finance Superuser',
  superuser_contract: 'Contract Superuser',
  admin: 'Administrator',
  director: 'Director',
  sales: 'Sales',
  accounting: 'Accounting',
  compliance: 'Compliance',
  operations: 'Operations',
  purchasing: 'Purchasing',
  standard: 'Standard User',
  readonly: 'Read Only',
  auditor: 'External Auditor',
  client_guest: 'Client Guest',
  supplier_guest: 'Supplier Guest',
};

/* ──────────────────────── Permisos (spec §7.2) ───────────────────────── */

export const PERMISSIONS = [
  'company.view',
  'company.manage',
  'user.manage',
  'document.create',
  'document.edit_draft',
  'document.issue_fiscal',  // emitir factura, nota de crédito o de débito
  'document.approve',
  'document.sign',
  'document.send',
  'document.download',
  'document.print',
  'document.correct',      // corregir documentos finalizados
  'document.void',         // anular
  'document.restore',      // restaurar versiones
  'period.reopen',         // reabrir periodos
  'price.modify',
  'payment.register',
  'payment.reassign',
  'bank.view',
  'kyc.view',
  'kyc.approve',
  'catalog.manage',
  'report.view',
  'audit.view',
  'settings.manage',
  'ai.use',
  'ai.apply',              // aplicar propuestas de la IA
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ALL: Permission[] = [...PERMISSIONS];

/** Matriz rol → permisos. */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  owner: ALL,
  superuser_business: ALL.filter((p) => p !== 'period.reopen'),
  superuser_finance: [
    'company.view', 'document.create', 'document.edit_draft', 'document.issue_fiscal',
    'document.approve',
    'document.send', 'document.download', 'document.print', 'document.correct',
    'document.void', 'document.restore', 'period.reopen', 'price.modify',
    'payment.register', 'payment.reassign', 'bank.view', 'report.view',
    'audit.view', 'ai.use', 'ai.apply',
  ],
  superuser_contract: [
    'company.view', 'document.create', 'document.edit_draft', 'document.approve',
    'document.sign', 'document.send', 'document.download', 'document.print',
    'document.correct', 'document.restore', 'kyc.view', 'report.view',
    'audit.view', 'ai.use', 'ai.apply',
  ],
  admin: [
    'company.view', 'company.manage', 'user.manage', 'document.create',
    'document.edit_draft', 'document.send', 'document.download', 'document.print',
    'catalog.manage', 'settings.manage', 'report.view', 'audit.view', 'ai.use',
  ],
  director: [
    'company.view', 'document.create', 'document.edit_draft', 'document.issue_fiscal',
    'document.approve', 'document.sign', 'document.send', 'document.download', 'document.print',
    'price.modify', 'bank.view', 'kyc.view', 'report.view', 'audit.view', 'ai.use', 'ai.apply',
  ],
  sales: [
    'company.view', 'document.create', 'document.edit_draft', 'document.send',
    'document.download', 'document.print', 'report.view', 'ai.use',
  ],
  accounting: [
    'company.view', 'document.create', 'document.edit_draft', 'document.issue_fiscal',
    'document.send',
    'document.download', 'document.print', 'payment.register', 'bank.view',
    'report.view', 'ai.use',
  ],
  compliance: [
    'company.view', 'document.download', 'kyc.view', 'kyc.approve',
    'report.view', 'audit.view', 'ai.use',
  ],
  operations: [
    'company.view', 'document.create', 'document.edit_draft', 'document.send',
    'document.download', 'document.print', 'ai.use',
  ],
  purchasing: [
    'company.view', 'document.create', 'document.edit_draft', 'document.send',
    'document.download', 'document.print', 'ai.use',
  ],
  standard: ['company.view', 'document.create', 'document.edit_draft', 'document.download', 'ai.use'],
  readonly: ['company.view', 'document.download'],
  auditor: ['company.view', 'document.download', 'report.view', 'audit.view', 'kyc.view'],
  client_guest: ['document.download', 'document.sign'],
  supplier_guest: ['document.download'],
};

export function permissionsForRole(role: Role): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

export function roleHas(role: Role, permission: Permission): boolean {
  return permissionsForRole(role).includes(permission);
}

/* ─────────────────── Tipos de documento (spec §11–§22) ───────────────── */

export const DOCUMENT_TYPES = [
  // Ciclo comercial
  'quote',            // Cotización / propuesta
  'contract',         // Contrato
  'invoice',          // Factura (tax / commercial / proforma / advance / final)
  'credit_note',      // Nota de crédito
  'debit_note',       // Nota de débito
  'payment',          // Cobro o pago
  'receipt',          // Recibo
  'expense',          // Gasto / ticket
  // Compras
  'purchase_request',
  'rfq',
  'purchase_order',
  'goods_receipt',
  'vendor_invoice',
  // Internacional
  'loi',              // Letter of Intent
  'icpo',             // Irrevocable Corporate Purchase Order
  'sco',              // Soft Corporate Offer
  'fco',              // Full Corporate Offer
  'ncnda',
  'imfpa',
  // Otros
  'delivery_note',
  'statement',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  quote: 'Quotation',
  contract: 'Contract',
  invoice: 'Invoice',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note',
  payment: 'Payment',
  receipt: 'Receipt',
  expense: 'Expense',
  purchase_request: 'Purchase Request',
  rfq: 'RFQ',
  purchase_order: 'Purchase Order',
  goods_receipt: 'Goods Receipt',
  vendor_invoice: 'Vendor Invoice',
  loi: 'LOI',
  icpo: 'ICPO',
  sco: 'Soft Corporate Offer',
  fco: 'Full Corporate Offer',
  ncnda: 'NCNDA',
  imfpa: 'IMFPA',
  delivery_note: 'Delivery Note',
  statement: 'Statement',
};

/* ───────────────────────── Estados de documento ──────────────────────── */

export const DOCUMENT_STATUSES = [
  'draft',        // Borrador — editable libremente
  'pending',      // Pendiente de aprobación
  'approved',     // Aprobado internamente
  'issued',       // Emitido / enviado — a partir de aquí NO se edita, se CORRIGE
  'accepted',     // Aceptado por la contraparte
  'rejected',
  'signed',
  'partially_paid',
  'paid',
  'overdue',
  'corrected',    // Sustituido por una versión corregida
  'void',         // Anulado (nunca borrado)
  'closed',
  'expired',
] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  pending: 'Pending Approval',
  approved: 'Approved',
  issued: 'Issued',
  accepted: 'Accepted',
  rejected: 'Rejected',
  signed: 'Signed',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  corrected: 'Corrected',
  void: 'Void',
  closed: 'Closed',
  expired: 'Expired',
};

/**
 * Estados a partir de los cuales el documento queda sellado: ya no existe
 * "Editar", solo "Corregir documento" (spec §25.2).
 */
export const SEALED_STATUSES: DocumentStatus[] = [
  'issued', 'accepted', 'signed', 'partially_paid', 'paid',
  'overdue', 'corrected', 'void', 'closed',
];

export function isSealed(status: DocumentStatus): boolean {
  return SEALED_STATUSES.includes(status);
}

/* ─────────────── Niveles de corrección (spec §25.3) ──────────────────── */

export const CORRECTION_LEVELS = {
  1: {
    label: 'Informative correction',
    description: 'Does not alter amounts or tax data.',
    fields: ['title', 'notes', 'reference', 'description', 'attachments', 'terms'],
    requiresApproval: false,
    permission: 'document.correct' as Permission,
  },
  2: {
    label: 'Tax correction',
    description: 'Affects tax-relevant data. Impact is recorded.',
    fields: ['issueDate', 'number', 'counterpartyId', 'legalName', 'taxNumber', 'country', 'period'],
    requiresApproval: true,
    permission: 'document.correct' as Permission,
  },
  3: {
    label: 'Financial correction',
    description: 'Alters amounts. May require a credit note and recalculates payments.',
    fields: ['quantity', 'unitPrice', 'discount', 'tax', 'total', 'currency', 'exchangeRate', 'lines'],
    requiresApproval: true,
    permission: 'document.correct' as Permission,
  },
} as const;
export type CorrectionLevel = 1 | 2 | 3;

export const CORRECTION_REASONS = [
  'error_fecha', 'error_concepto', 'error_cliente', 'error_direccion',
  'error_fiscal', 'error_importe', 'error_impuesto', 'error_moneda',
  'error_banco', 'otro',
] as const;
export type CorrectionReason = (typeof CORRECTION_REASONS)[number];

export const CORRECTION_REASON_LABELS: Record<CorrectionReason, string> = {
  error_fecha: 'Date error',
  error_concepto: 'Item error',
  error_cliente: 'Client error',
  error_direccion: 'Address error',
  error_fiscal: 'Tax data error',
  error_importe: 'Amount error',
  error_impuesto: 'Tax error',
  error_moneda: 'Currency error',
  error_banco: 'Bank error',
  otro: 'Other',
};

/**
 * Campos que la IA NUNCA modifica en silencio (spec §13).
 * Cualquier propuesta que los toque exige confirmación explícita del usuario
 * con vista de diferencias.
 */
export const AI_PROTECTED_FIELDS = [
  'totalMinor', 'subtotalMinor', 'taxMinor', 'unitPriceMinor', 'quantity',
  'currency', 'exchangeRate', 'taxPct', 'discountPct',
  'issueDate', 'dueDate', 'validUntil',
  'bankAccountId', 'iban', 'swift',
  'counterpartyId', 'companyId',
  'jurisdiction', 'governingLaw', 'commissionPct',
] as const;

/* ───────────────── Tipos de contraparte (spec §8.1) ──────────────────── */

export const COUNTERPARTY_KINDS = [
  'client', 'supplier', 'buyer', 'seller', 'broker', 'mandate', 'agent',
  'refinery', 'carrier', 'bank', 'insurer', 'consultant', 'partner',
] as const;
export type CounterpartyKind = (typeof COUNTERPARTY_KINDS)[number];

export const COUNTERPARTY_KIND_LABELS: Record<CounterpartyKind, string> = {
  client: 'Client', supplier: 'Supplier', buyer: 'Buyer', seller: 'Seller',
  broker: 'Broker', mandate: 'Mandate', agent: 'Agent', refinery: 'Refinery',
  carrier: 'Carrier', bank: 'Bank', insurer: 'Insurer',
  consultant: 'Consultant', partner: 'Partner',
};

/* ──────────────────────── KYC (spec §9.2) ───────────────────────────── */

export const KYC_STATUSES = [
  'not_started', 'requested', 'pending', 'in_review',
  'more_info', 'approved', 'rejected', 'expired', 'suspended',
] as const;
export type KycStatus = (typeof KYC_STATUSES)[number];

export const KYC_STATUS_LABELS: Record<KycStatus, string> = {
  not_started: 'Not Started', requested: 'Requested', pending: 'Pending',
  in_review: 'In Review', more_info: 'More Info Needed', approved: 'Approved',
  rejected: 'Rejected', expired: 'Expired', suspended: 'Suspended',
};

/* ───────────── Documentos de cumplimiento (spec §5.1) ────────────────── */

export const COMPLIANCE_DOC_TYPES = [
  'trade_license', 'certificate_of_incorporation', 'moa', 'articles_of_association',
  'share_certificate', 'establishment_card', 'tax_certificate', 'vat_certificate',
  'passport', 'visa', 'emirates_id', 'work_permit', 'office_contract',
  'lease_agreement', 'insurance', 'sector_license', 'import_export_certificate',
  'proof_of_address', 'bank_certificate', 'source_of_funds',
] as const;
export type ComplianceDocType = (typeof COMPLIANCE_DOC_TYPES)[number];

export const COMPLIANCE_DOC_LABELS: Record<ComplianceDocType, string> = {
  trade_license: 'Trade License',
  certificate_of_incorporation: 'Certificate of Incorporation',
  moa: 'MOA',
  articles_of_association: 'Articles of Association',
  share_certificate: 'Share Certificate',
  establishment_card: 'Establishment Card',
  tax_certificate: 'Tax Certificate',
  vat_certificate: 'VAT Certificate',
  passport: 'Passport',
  visa: 'Visa',
  emirates_id: 'Emirates ID',
  work_permit: 'Work Permit',
  office_contract: 'Office Contract',
  lease_agreement: 'Lease Agreement',
  insurance: 'Insurance',
  sector_license: 'Sector License',
  import_export_certificate: 'Import/Export Certificate',
  proof_of_address: 'Proof of Address',
  bank_certificate: 'Bank Certificate',
  source_of_funds: 'Source of Funds',
};

/** Umbrales de alerta de caducidad, en días (spec §5.4). */
export const EXPIRY_THRESHOLDS_DAYS = [120, 90, 60, 30, 15, 7, 0] as const;

/* ─────────────────── Etapas de Deal Room (spec §21) ──────────────────── */

export const DEAL_STAGES = [
  'opportunity', 'kyc', 'loi', 'offer', 'negotiation', 'contract',
  'bank', 'logistics', 'inspection', 'delivery', 'invoicing',
  'collection', 'closed', 'cancelled',
] as const;
export type DealStage = (typeof DEAL_STAGES)[number];

export const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  opportunity: 'Opportunity', kyc: 'KYC', loi: 'LOI', offer: 'Offer',
  negotiation: 'Negotiation', contract: 'Contract', bank: 'Bank',
  logistics: 'Logistics', inspection: 'Inspection', delivery: 'Delivery',
  invoicing: 'Invoicing', collection: 'Collection', closed: 'Closed',
  cancelled: 'Cancelled',
};

/* ─────────────────────────── Impuestos ──────────────────────────────── */

export const TAX_KINDS = [
  'standard', 'zero_rated', 'exempt', 'reverse_charge', 'withholding', 'out_of_scope',
] as const;
export type TaxKind = (typeof TAX_KINDS)[number];
